/**
 * O acordeão "Texto explicativo: As 12 Estações da Coloração Pessoal", FECHADO ao abrir a aba,
 * com o texto palavra por palavra do legado (`admin.html:2698-2724`).
 *
 * POR QUE `details` E NÃO UM BOTÃO: o Chassi não tem acordeão (medido no `index.ts` de
 * @complexity/ui em 19/09/2026), e o lint recusa `button` escrito no app. O `details` com
 * `summary` é o acordeão do próprio navegador: abre e fecha por clique, toque e teclado (Enter e
 * Espaço), e anuncia aberto ou fechado ao leitor de tela sem uma linha a mais. No legado o
 * cabeçalho é um `div` que só abre por clique (`5056-5058`); abrir também pelo teclado é o mesmo
 * gesto chegando a quem não usa mouse.
 *
 * ABERTO OU FECHADO NÃO É DESTE COMPONENTE: a aba desmonta a cada troca, e o legado mantém o
 * acordeão como a pessoa deixou até o `carregarQuestionarios` fechá-lo (`5021`). Por isso o estado
 * vem de fora, de `acordeao.ts`, que explica cada gatilho, e aqui só se desenha e se avisa o clique.
 */

// O desenho da seta, do Chassi: a "▾" do legado é caractere, e ícone aqui é SVG.
import { IconeChevron } from '@complexity/ui';

/** Um grupo de estações: o título do grupo e as três estações com a descrição. */
interface GrupoDeEstacoes {
  readonly grupo: string;
  readonly estacoes: readonly { readonly nome: string; readonly texto: string }[];
}

/** O texto do legado, na ordem da tela (`admin.html:2704-2722`). */
const GRUPOS: readonly GrupoDeEstacoes[] = [
  {
    grupo: 'Grupo das Primaveras (Cores Claras, Quentes e Luminosas)',
    estacoes: [
      {
        nome: 'Primavera Quente (ou Pura / Verdadeira):',
        texto: 'A temperatura quente é a característica principal. Os tons são super dourados, alegres e radiantes.',
      },
      { nome: 'Primavera Clara:', texto: 'A luminosidade e a leveza se destacam. Cores mais pastel e delicadas.' },
      {
        nome: 'Primavera Brilhante (ou Intensa):',
        texto: 'A alta saturação é o forte. Cores extremamente vivas, elétricas e cheias de contraste.',
      },
    ],
  },
  {
    grupo: 'Grupo dos Outonos (Cores Escuras, Quentes e Opacas)',
    estacoes: [
      {
        nome: 'Outono Quente (ou Puro / Verdadeiro):',
        texto: 'Focado no calor extremo das cores. Muito uso de tons terrosos, mostarda, oliva e caramelo.',
      },
      {
        nome: 'Outono Escuro (ou Profundo):',
        texto: 'A profundidade manda aqui. Cores mais densas, fechadas e sóbrias (como vinho e marrom escuro).',
      },
      { nome: 'Outono Suave:', texto: 'A opacidade é a chave. Tons mais apagados, elegantes, sem brilho excessivo.' },
    ],
  },
  {
    grupo: 'Grupo dos Invernos (Cores Escuras, Frias e Intensas)',
    estacoes: [
      {
        nome: 'Inverno Frio (ou Puro / Verdadeiro):',
        texto: 'A temperatura totalmente fria é o foco. Tons azulados, roxos puros e o clássico preto e branco absoluto.',
      },
      { nome: 'Inverno Escuro (ou Profundo):', texto: 'Muito escuro e de alto contraste. Cores profundas e dramáticas.' },
      {
        nome: 'Inverno Brilhante (ou Intenso):',
        texto: 'Super vibrante, frio e cristalino. Cores puras com muito brilho.',
      },
    ],
  },
  {
    grupo: 'Grupo dos Verões (Cores Claras, Frias e Suaves)',
    estacoes: [
      {
        nome: 'Verão Frio (ou Puro / Verdadeiro):',
        texto: 'O foco é o subtom totalmente frio e azulado. Cores calmas e refrescantes.',
      },
      { nome: 'Verão Claro:', texto: 'Cores muito lavadas, suaves, iluminadas e de tom pastel.' },
      {
        nome: 'Verão Suave:',
        texto: 'Mistura o frio com uma dose extra de cinza na composição. Cores foscas, discretas e aveludadas.',
      },
    ],
  },
];

export function TextoExplicativo({
  aberto,
  aoMudar,
}: {
  // Aberto ou fechado, lido do estado que vive com a página (`acordeao.ts`).
  readonly aberto: boolean;
  // Chamado quando o navegador abre ou fecha o `details` pelo gesto da pessoa.
  readonly aoMudar: (aberto: boolean) => void;
}) {
  return (
    // `group` deixa a seta girar quando o `details` está aberto, como o `rotate(180deg)` do legado.
    // O `toggle` chega depois de o navegador já ter trocado o `open`, então ler o `open` do próprio
    // elemento dá o estado novo, venha do clique, do toque ou do teclado.
    <details
      className="group rounded-cx-md border border-borda"
      data-testid="acordeon-explicativo-coloracao"
      open={aberto}
      onToggle={(evento) => aoMudar(evento.currentTarget.open)}
    >
      {/* `list-none` e o seletor do marcador tiram o triângulo que o navegador desenha sozinho. */}
      <summary className="flex min-h-toque cursor-pointer list-none items-center justify-between gap-cx-2 px-cx-4 py-cx-3 text-cx-base text-texto [&::-webkit-details-marker]:hidden">
        <span>Texto explicativo: As 12 Estações da Coloração Pessoal</span>
        <IconeChevron
          aria-hidden="true"
          className="h-cx-4 w-cx-4 shrink-0 text-texto-suave transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="flex flex-col gap-cx-1 px-cx-4 pb-cx-4">
        {GRUPOS.map((grupo, indice) => (
          <div key={grupo.grupo} className="flex flex-col gap-cx-1">
            {/* O título do grupo em negrito, com respiro acima a partir do segundo (`1482-1483`). */}
            <p className={`text-cx-sm font-semibold text-texto ${indice > 0 ? 'mt-cx-3' : ''}`}>{grupo.grupo}</p>
            {grupo.estacoes.map((estacao) => (
              <p key={estacao.nome} className="text-cx-xs text-texto">
                <strong>{estacao.nome}</strong> {estacao.texto}
              </p>
            ))}
          </div>
        ))}
      </div>
    </details>
  );
}
